import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  VStack,
  Wrap,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useV2PathListQuery } from "features/Path/hooks";
import { Path } from "features/Path/models";
import SelectPathsMenuButton from "features/Path/Components/Button/SelectPathsMenuButton";

interface PreviewControlsProps {
  selectedPathIds: string[];
  onPathIdsChange: (ids: string[]) => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  onRunPreview: () => void;
  isLoading: boolean;
}

export default function PreviewControls({
  selectedPathIds,
  onPathIdsChange,
  startDate,
  onStartDateChange,
  onRunPreview,
  isLoading,
}: PreviewControlsProps) {
  const { data: allPaths } = useV2PathListQuery("");

  const selectedPaths: Path[] = (allPaths || []).filter((p: Path) => selectedPathIds.includes(p.id));

  const handleAddPath = useCallback(
    (id: string) => {
      if (!selectedPathIds.includes(id)) {
        onPathIdsChange([...selectedPathIds, id]);
      }
    },
    [selectedPathIds, onPathIdsChange],
  );

  const handleRemovePath = useCallback(
    (id: string) => {
      onPathIdsChange(selectedPathIds.filter((pid) => pid !== id));
    },
    [selectedPathIds, onPathIdsChange],
  );

  const canRun = selectedPathIds.length > 0 && !!startDate;

  return (
    <VStack align="stretch" spacing="4" p="4" bg="gray.50" borderRadius="md">
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium">
          Paths
        </FormLabel>
        <Wrap spacing="2" align="center">
          {selectedPaths.map((path) => (
            <Tag key={path.id} size="md" colorScheme="blue" variant="subtle">
              <TagLabel>{path.name}</TagLabel>
              <TagCloseButton onClick={() => handleRemovePath(path.id)} />
            </Tag>
          ))}
          <SelectPathsMenuButton selectedPathIds={selectedPathIds} addPathId={handleAddPath} includeCreateNew={false} />
        </Wrap>
      </FormControl>
      <HStack align="end" spacing="3">
        <FormControl maxW="240px">
          <FormLabel fontSize="sm" fontWeight="medium">
            Hypothetical start date
          </FormLabel>
          <Input type="date" value={startDate} onChange={(e) => onStartDateChange(e.target.value)} />
        </FormControl>
        <Box>
          <Button colorScheme="blue" onClick={onRunPreview} isDisabled={!canRun || isLoading} isLoading={isLoading}>
            Generate preview
          </Button>
        </Box>
      </HStack>
    </VStack>
  );
}
